import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import {
  FileBarChart, Plus, Trash2, Eye, Loader2, Sparkles, Zap, TrendingUp,
  Shield, Settings, BarChart3, AlertTriangle, CheckCircle, Clock, Users,
  Target, Mail, Search, Printer, Calendar, Brain, ArrowUpRight, ArrowDownRight,
  Crosshair, Gauge, ChevronRight, Activity, Lightbulb, TrendingDown,
  CircleDot, Flame, Layers, Download
} from 'lucide-react';
import ShareViaEmail from '@/components/ShareViaEmail';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReportsPage = () => {
  const { token } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [period, setPeriod] = useState('30d');
  const [isManualOpen, setIsManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ title: '', content: '', region: '', report_type: 'general' });
  const [accuracyHistory, setAccuracyHistory] = useState([]);
  const [createdActions, setCreatedActions] = useState({});
  const [creatingAction, setCreatingAction] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchReports(); fetchAccuracyHistory(); }, [search]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports`, { params: { search }, headers });
      setReports(res.data);
      // Auto-select the latest generated report
      const generated = res.data.find(r => r.generated);
      if (generated && !selectedReport) setSelectedReport(generated);
    } catch { /* */ } finally { setLoading(false); }
  };

  const fetchAccuracyHistory = async () => {
    try {
      const res = await axios.get(`${API}/reports/accuracy-history`, { headers });
      setAccuracyHistory(res.data);
    } catch { /* */ }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/reports/generate`, { report_type: 'comprehensive', period }, { headers });
      toast.success('Report generated!');
      setSelectedReport(res.data);
      setCreatedActions({});
      fetchReports();
      fetchAccuracyHistory();
    } catch { toast.error('Failed to generate report'); }
    finally { setGenerating(false); }
  };

  const handleCreateActionTask = async (action, index) => {
    setCreatingAction(index);
    try {
      const res = await axios.post(`${API}/reports/action-to-task`, {
        action: action.action,
        priority: action.priority,
        expected_impact: action.expected_impact,
        effort: action.effort,
        kpi_target: action.kpi_target,
        confidence: action.confidence,
        report_id: r?.report_id || '',
      }, { headers });
      setCreatedActions(prev => ({ ...prev, [index]: res.data.task_id }));
      toast.success(`Task created: "${action.action.substring(0, 50)}..."`);
    } catch { toast.error('Failed to create task'); }
    finally { setCreatingAction(null); }
  };

  const handleManualCreate = async () => {
    if (!manualForm.title) { toast.error('Title required'); return; }
    try {
      await axios.post(`${API}/reports`, manualForm, { headers });
      toast.success('Report created');
      setIsManualOpen(false);
      setManualForm({ title: '', content: '', region: '', report_type: 'general' });
      fetchReports();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (reportId) => {
    try {
      await axios.delete(`${API}/reports/${reportId}`, { headers });
      toast.success('Deleted');
      if (selectedReport?.report_id === reportId) setSelectedReport(null);
      fetchReports();
    } catch { toast.error('Failed'); }
  };

  const handlePrint = () => {
    if (!selectedReport) return;
    const r = selectedReport;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<html><head><title>${r.title}</title><style>body{font-family:system-ui;padding:40px;max-width:800px;margin:0 auto}h1{color:#002FA7;font-size:24px}h2{color:#333;font-size:18px;margin-top:20px}.kpi{display:inline-block;padding:8px 16px;margin:4px;background:#f3f4f6;border-radius:8px}.content{white-space:pre-wrap;line-height:1.6}</style></head><body><h1>${r.title}</h1><p style="color:#666">Generated ${r.created_at?.substring(0,10)} | Period: ${r.period || 'N/A'}</p><div class="content">${r.content || ''}</div></body></html>`);
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownloadPdf = async () => {
    if (!selectedReport) return;
    try {
      const res = await axios.get(`${API}/reports/${selectedReport.report_id}/export-pdf`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `report-${selectedReport.report_id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleShareReport = async () => {
    if (!selectedReport) return;
    const emails = prompt('Enter email addresses (comma-separated):');
    if (!emails) return;
    const message = prompt('Add a personal message (optional):') || '';
    try {
      const res = await axios.post(`${API}/reports/${selectedReport.report_id}/share`, { to_emails: emails.split(',').map(e => e.trim()).filter(Boolean), message }, { headers });
      toast.success(res.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Share failed'); }
  };

  const r = selectedReport;
  const perf = r?.performance || {};
  const comp = r?.compliance || {};
  const ops = r?.operations || {};
  const analytics = r?.analytics || {};

  const COLORS = ['#FF3B30', '#FF9500', '#007AFF', '#34C759', '#AF52DE', '#FF2D55'];

  return (
    <div data-testid="reports-page" className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Reports & Analytics</h1>
          <p className="text-sm text-[#4B5563]">Dynamic reports with KPIs, metrics, and visual analytics</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Report Dropdown Selector */}
          {reports.length > 0 && (
            <Select value={selectedReport?.report_id || ''} onValueChange={v => { const rep = reports.find(r => r.report_id === v); if (rep) setSelectedReport(rep); }}>
              <SelectTrigger className="w-[280px] text-xs" data-testid="report-dropdown">
                <div className="flex items-center gap-1.5 truncate">
                  {selectedReport?.generated ? <Sparkles className="w-3 h-3 text-[#002FA7] flex-shrink-0" /> : <FileBarChart className="w-3 h-3 text-[#4B5563] flex-shrink-0" />}
                  <SelectValue placeholder="Select a report" />
                </div>
              </SelectTrigger>
              <SelectContent>
                {reports.map(rep => (
                  <SelectItem key={rep.report_id} value={rep.report_id}>
                    <div className="flex items-center gap-2">
                      {rep.generated ? <Sparkles className="w-3 h-3 text-[#002FA7]" /> : <FileBarChart className="w-3 h-3 text-[#4B5563]" />}
                      <span className="truncate">{rep.title}</span>
                      <span className="text-[#9CA3AF] text-[10px] ml-1">{rep.created_at?.substring(0, 10)}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[90px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">7 Days</SelectItem>
              <SelectItem value="30d">30 Days</SelectItem>
              <SelectItem value="90d">90 Days</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" className="bg-[#002FA7] hover:bg-[#00227A] text-xs" onClick={handleGenerate} disabled={generating} data-testid="generate-report-btn">
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
            Generate
          </Button>
          <Button size="sm" variant="outline" className="text-xs" onClick={() => setIsManualOpen(true)}><Plus className="w-3.5 h-3.5 mr-1" />Manual</Button>
          {selectedReport && <Button size="sm" variant="outline" className="text-xs" onClick={handleDownloadPdf} data-testid="export-pdf-btn"><Download className="w-3.5 h-3.5 mr-1" />PDF</Button>}
          {selectedReport && <Button size="sm" variant="outline" className="text-xs" onClick={handleShareReport} data-testid="share-email-btn"><Mail className="w-3.5 h-3.5 mr-1" />Share</Button>}
        </div>
      </div>

      {!r ? (
        <Card className="border-[#E5E7EB]"><CardContent className="p-12 text-center text-[#9CA3AF]">
          <FileBarChart className="w-12 h-12 mx-auto mb-3 text-[#E5E7EB]" />
          <p className="text-sm mb-3">No reports yet. Click "Generate" to create one automatically.</p>
        </CardContent></Card>
      ) : r.generated ? (
        <>
          {/* Report Header & Actions */}
          <Card className="border-[#E5E7EB]">
            <CardContent className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#002FA7]" />
                <span className="text-sm font-medium">{r.title}</span>
                <Badge variant="secondary" className="text-[9px]">{r.period}</Badge>
                <Badge className="text-[9px] bg-[#34C759]/10 text-[#34C759]">Auto-generated</Badge>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handlePrint}><Printer className="w-3 h-3 mr-1" />Print</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShareOpen(true)}><Mail className="w-3 h-3 mr-1" />Share</Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs text-[#FF3B30]" onClick={() => handleDelete(r.report_id)}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </CardContent>
          </Card>

          {/* KPI Cards Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="kpi-cards">
            {[
              { label: 'Task Completion', value: `${perf.task_completion_rate || 0}%`, icon: CheckCircle, color: '#34C759' },
              { label: 'Knowledge Health', value: `${perf.knowledge_health_score || 0}`, icon: Shield, color: perf.knowledge_health_score >= 70 ? '#34C759' : '#FF9500' },
              { label: 'Overdue Tasks', value: perf.overdue_tasks || 0, icon: AlertTriangle, color: (perf.overdue_tasks || 0) > 0 ? '#FF3B30' : '#34C759' },
              { label: 'Critical Open', value: comp.critical_tasks_open || 0, icon: Target, color: (comp.critical_tasks_open || 0) > 0 ? '#FF3B30' : '#34C759' },
              { label: 'Decision Rate', value: `${perf.decision_follow_up_rate || 0}%`, icon: Target, color: '#007AFF' },
              { label: 'Transcription', value: `${perf.transcription_rate || 0}%`, icon: FileBarChart, color: '#AF52DE' },
              { label: 'Anomalies', value: comp.unresolved_anomalies || 0, icon: AlertTriangle, color: '#FF9500' },
            ].map((kpi, i) => {
              const Icon = kpi.icon;
              return (
                <Card key={i} className="border-[#E5E7EB]" data-testid={`kpi-${i}`}>
                  <CardContent className="p-3 text-center">
                    <Icon className="w-4 h-4 mx-auto mb-1" style={{ color: kpi.color }} />
                    <p className="text-xl font-bold" style={{ color: kpi.color }}>{kpi.value}</p>
                    <p className="text-[10px] text-[#9CA3AF]">{kpi.label}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* All Sections - Single Scrollable View */}
          <div className="space-y-6">

            {/* Performance Section */}
            <div data-testid="section-performance">
              <h2 className="text-base font-semibold text-[#0A0A0A] flex items-center gap-2 mb-3 pb-2 border-b border-[#E5E7EB]"><TrendingUp className="w-4 h-4 text-[#007AFF]" />Performance</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Activity Trends */}
                <Card className="border-[#E5E7EB]" data-testid="trends-chart">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#007AFF]" />Activity Trends</CardTitle></CardHeader>
                  <CardContent>
                    {r.trends?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={r.trends}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="tasks" name="Tasks" fill="#007AFF" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="articles" name="Articles" fill="#AF52DE" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="webinars" name="Webinars" fill="#FF2D55" radius={[4, 4, 0, 0]} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <p className="text-xs text-[#9CA3AF] text-center py-8">No trend data available</p>}
                  </CardContent>
                </Card>

                {/* Task Status Pie */}
                <Card className="border-[#E5E7EB]" data-testid="status-chart">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#34C759]" />Task Status</CardTitle></CardHeader>
                  <CardContent>
                    {r.status_chart?.some(s => s.value > 0) ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie data={r.status_chart} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                            {r.status_chart.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : <p className="text-xs text-[#9CA3AF] text-center py-8">No task data</p>}
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Compliance Section */}
            <div data-testid="section-compliance">
              <h2 className="text-base font-semibold text-[#0A0A0A] flex items-center gap-2 mb-3 pb-2 border-b border-[#E5E7EB]"><Shield className="w-4 h-4 text-[#5856D6]" />Compliance</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="border-[#E5E7EB]" data-testid="priority-chart">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#FF9500]" />Task Priority Distribution</CardTitle></CardHeader>
                  <CardContent>
                    {r.priority_chart?.some(p => p.value > 0) ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={r.priority_chart} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={70} />
                          <Tooltip />
                          <Bar dataKey="value" name="Tasks" radius={[0, 4, 4, 0]}>
                            {r.priority_chart.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <p className="text-xs text-[#9CA3AF] text-center py-8">No priority data</p>}
                  </CardContent>
                </Card>

                <Card className="border-[#E5E7EB]" data-testid="compliance-metrics">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Shield className="w-4 h-4 text-[#5856D6]" />Compliance Metrics</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    {[
                      { label: 'Knowledge Audit Coverage', value: `${comp.knowledge_audit_coverage || 0}%`, color: '#34C759', max: 100 },
                      { label: 'Overdue Tasks', value: comp.overdue_tasks_count || 0, color: '#FF3B30', max: 20, invert: true },
                      { label: 'Critical Tasks Open', value: comp.critical_tasks_open || 0, color: '#FF9500', max: 10, invert: true },
                      { label: 'Unresolved Anomalies', value: comp.unresolved_anomalies || 0, color: '#AF52DE', max: 20, invert: true },
                      { label: 'Pending HR Workflows', value: comp.pending_workflows || 0, color: '#007AFF', max: 10, invert: true },
                    ].map((m, i) => {
                      const pct = m.invert ? Math.max(0, 100 - ((m.value / m.max) * 100)) : Math.min(100, (typeof m.value === 'string' ? parseInt(m.value) : m.value));
                      return (
                        <div key={i}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-[#4B5563]">{m.label}</span>
                            <span className="font-medium" style={{ color: m.color }}>{m.value}</span>
                          </div>
                          <div className="w-full h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: m.color }} />
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Operations Section */}
            <div data-testid="section-operations">
              <h2 className="text-base font-semibold text-[#0A0A0A] flex items-center gap-2 mb-3 pb-2 border-b border-[#E5E7EB]"><Settings className="w-4 h-4 text-[#FF9500]" />Operations</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="operations-metrics">
                {[
                  { label: 'Total Users', value: ops.total_users || 0, icon: Users, color: '#007AFF' },
                  { label: 'Teams', value: ops.total_teams || 0, icon: Users, color: '#00C7BE' },
                  { label: 'Active Webinars', value: ops.active_webinars || 0, icon: Calendar, color: '#FF2D55' },
                  { label: 'Recurring Tasks', value: ops.recurring_tasks || 0, icon: Clock, color: '#FF9500' },
                  { label: 'Knowledge Maps', value: ops.knowledge_maps || 0, icon: Target, color: '#AF52DE' },
                  { label: 'Recordings', value: ops.total_recordings || 0, icon: FileBarChart, color: '#007AFF' },
                  { label: 'Clips', value: ops.total_clips || 0, icon: Sparkles, color: '#FF9500' },
                  { label: 'Annotations', value: ops.total_annotations || 0, icon: FileBarChart, color: '#34C759' },
                ].map((m, i) => {
                  const Icon = m.icon;
                  return (
                    <Card key={i} className="border-[#E5E7EB]">
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: m.color + '15' }}>
                          <Icon className="w-5 h-5" style={{ color: m.color }} />
                        </div>
                        <div>
                          <p className="text-xl font-bold text-[#0A0A0A]">{m.value}</p>
                          <p className="text-[10px] text-[#9CA3AF]">{m.label}</p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Analytics Section */}
            <div data-testid="section-analytics">
              <h2 className="text-base font-semibold text-[#0A0A0A] flex items-center gap-2 mb-3 pb-2 border-b border-[#E5E7EB]"><BarChart3 className="w-4 h-4 text-[#002FA7]" />Analytics</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card className="border-[#E5E7EB]" data-testid="analytics-overview">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#002FA7]" />Knowledge Base Overview</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={[
                        { name: 'Articles', value: analytics.total_articles || 0, fill: '#AF52DE' },
                        { name: 'Tasks', value: analytics.total_tasks || 0, fill: '#007AFF' },
                        { name: 'Notes', value: analytics.total_notes || 0, fill: '#34C759' },
                        { name: 'Decisions', value: analytics.total_decisions || 0, fill: '#FF9500' },
                        { name: 'Webinars', value: analytics.total_webinars || 0, fill: '#FF2D55' },
                        { name: 'Workflows', value: analytics.total_workflows || 0, fill: '#00C7BE' },
                      ]}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                          {[{ fill: '#AF52DE' }, { fill: '#007AFF' }, { fill: '#34C759' }, { fill: '#FF9500' }, { fill: '#FF2D55' }, { fill: '#00C7BE' }].map((e, idx) => <Cell key={idx} fill={e.fill} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card className="border-[#E5E7EB]" data-testid="analytics-relationships">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Target className="w-4 h-4 text-[#FF9500]" />Knowledge Relationships</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 rounded-lg bg-[#F9FAFB] text-center">
                        <p className="text-2xl font-bold text-[#002FA7]">{analytics.total_relationships || 0}</p>
                        <p className="text-[10px] text-[#9CA3AF]">PKM Relationships</p>
                      </div>
                      <div className="p-3 rounded-lg bg-[#F9FAFB] text-center">
                        <p className="text-2xl font-bold text-[#FF9500]">{analytics.total_decisions || 0}</p>
                        <p className="text-[10px] text-[#9CA3AF]">Decisions Tracked</p>
                      </div>
                    </div>
                    <div className="text-xs text-[#4B5563] leading-relaxed bg-[#F9FAFB] p-3 rounded-lg">
                      <p className="font-medium text-[#0A0A0A] mb-1">Knowledge Graph Health</p>
                      <p>{analytics.total_notes || 0} PKM notes connected by {analytics.total_relationships || 0} relationships across {analytics.total_decisions || 0} tracked decisions.</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Predictive Intelligence Section */}
            <div className="space-y-4" data-testid="section-predictive">
              <h2 className="text-base font-semibold text-[#0A0A0A] flex items-center gap-2 mb-3 pb-2 border-b border-[#E5E7EB]"><Brain className="w-4 h-4 text-[#AF52DE]" />Predictive Intelligence</h2>
              {/* Executive Summary */}
              <Card className="border-[#E5E7EB]" data-testid="ai-summary">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><Brain className="w-4 h-4 text-[#002FA7]" />Executive Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-[#4B5563] leading-relaxed bg-[#F9FAFB] p-4 rounded-lg border border-[#E5E7EB]" data-testid="ai-summary-content">
                    {r.content || 'No AI summary available.'}
                  </p>
                </CardContent>
              </Card>

              {/* Prediction Accuracy Tracking */}
              {r.prediction_tracking && (
                <Card className="border-[#E5E7EB]" data-testid="prediction-tracking-card">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm flex items-center gap-2"><Crosshair className="w-4 h-4 text-[#5856D6]" />Prediction Accuracy</CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge className="text-xs px-2 py-0.5" style={{
                          backgroundColor: r.prediction_tracking.overall_accuracy >= 70 ? '#34C75915' : r.prediction_tracking.overall_accuracy >= 40 ? '#FF950015' : '#FF3B3015',
                          color: r.prediction_tracking.overall_accuracy >= 70 ? '#34C759' : r.prediction_tracking.overall_accuracy >= 40 ? '#FF9500' : '#FF3B30',
                        }} data-testid="overall-accuracy-badge">
                          {r.prediction_tracking.overall_accuracy}% Overall Accuracy
                        </Badge>
                        <span className="text-[10px] text-[#9CA3AF]">vs report from {r.prediction_tracking.previous_report_date?.substring(0, 10)}</span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Accuracy Summary Row */}
                    <div className="grid grid-cols-4 gap-3">
                      <div className="p-3 rounded-lg bg-[#F9FAFB] text-center" data-testid="tracking-accuracy-score">
                        <p className="text-2xl font-bold" style={{ color: r.prediction_tracking.overall_accuracy >= 70 ? '#34C759' : r.prediction_tracking.overall_accuracy >= 40 ? '#FF9500' : '#FF3B30' }}>
                          {r.prediction_tracking.overall_accuracy}%
                        </p>
                        <p className="text-[10px] text-[#9CA3AF]">Accuracy Score</p>
                      </div>
                      <div className="p-3 rounded-lg bg-[#34C759]/5 text-center" data-testid="tracking-accurate-count">
                        <p className="text-2xl font-bold text-[#34C759]">{r.prediction_tracking.accurate_count}</p>
                        <p className="text-[10px] text-[#9CA3AF]">Accurate</p>
                      </div>
                      <div className="p-3 rounded-lg bg-[#FF9500]/5 text-center" data-testid="tracking-close-count">
                        <p className="text-2xl font-bold text-[#FF9500]">{r.prediction_tracking.close_count}</p>
                        <p className="text-[10px] text-[#9CA3AF]">Close</p>
                      </div>
                      <div className="p-3 rounded-lg bg-[#FF3B30]/5 text-center" data-testid="tracking-missed-count">
                        <p className="text-2xl font-bold text-[#FF3B30]">{r.prediction_tracking.missed_count}</p>
                        <p className="text-[10px] text-[#9CA3AF]">Missed</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Metric Comparisons */}
                      <div className="space-y-2">
                        <h4 className="text-xs font-semibold text-[#0A0A0A] flex items-center gap-1.5 mb-2"><BarChart3 className="w-3.5 h-3.5 text-[#007AFF]" />Metric Comparisons</h4>
                        {(r.prediction_tracking.metric_comparisons || []).map((mc, i) => {
                          const accColor = mc.accuracy === 'accurate' ? '#34C759' : mc.accuracy === 'close' ? '#FF9500' : '#FF3B30';
                          const accIcon = mc.accuracy === 'accurate' ? CheckCircle : mc.accuracy === 'close' ? Target : AlertTriangle;
                          const AccIcon = accIcon;
                          return (
                            <div key={i} className="p-2.5 rounded-lg border border-[#E5E7EB] bg-white flex items-center gap-3" data-testid={`metric-comparison-${i}`}>
                              <AccIcon className="w-4 h-4 flex-shrink-0" style={{ color: accColor }} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs font-medium text-[#0A0A0A]">{mc.metric}</span>
                                  <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: accColor + '15', color: accColor }}>{mc.accuracy}</Badge>
                                </div>
                                <div className="flex items-center gap-3 text-[10px]">
                                  <span className="text-[#9CA3AF]">Predicted: <span className="font-medium text-[#4B5563]">{mc.predicted}%</span></span>
                                  <span className="text-[#9CA3AF]">Actual: <span className="font-medium text-[#0A0A0A]">{mc.actual}%</span></span>
                                  <span style={{ color: mc.variance >= 0 ? '#34C759' : '#FF3B30' }} className="font-medium">
                                    {mc.variance >= 0 ? '+' : ''}{mc.variance}%
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {/* Scenario Landing */}
                        <div className="p-2.5 rounded-lg border border-dashed border-[#5856D6]/30 bg-[#5856D6]/5 flex items-center gap-2" data-testid="landed-scenario">
                          <Layers className="w-4 h-4 text-[#5856D6]" />
                          <span className="text-xs text-[#4B5563]">Actuals landed in the <span className="font-bold text-[#5856D6]">{r.prediction_tracking.landed_scenario}</span> scenario band</span>
                        </div>
                      </div>

                      {/* Prediction Insight Results */}
                      <div className="space-y-2">
                        <h4 className="text-xs font-semibold text-[#0A0A0A] flex items-center gap-1.5 mb-2"><Activity className="w-3.5 h-3.5 text-[#AF52DE]" />Forecast Results</h4>
                        {(r.prediction_tracking.insight_results || []).map((ir, i) => (
                          <div key={i} className="p-2.5 rounded-lg border border-[#E5E7EB] bg-white flex items-center gap-3" data-testid={`insight-result-${i}`}>
                            {ir.came_true ? (
                              <CheckCircle className="w-4 h-4 text-[#34C759] flex-shrink-0" />
                            ) : (
                              <AlertTriangle className="w-4 h-4 text-[#FF3B30] flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-[#0A0A0A] font-medium truncate">{ir.title}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[10px] text-[#9CA3AF]">{ir.probability}% predicted</span>
                                <Badge className="text-[9px] px-1.5 py-0" style={{
                                  backgroundColor: ir.came_true ? '#34C75915' : '#FF3B3015',
                                  color: ir.came_true ? '#34C759' : '#FF3B30'
                                }}>{ir.came_true ? 'Confirmed' : 'Did not occur'}</Badge>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Accuracy Trend Chart */}
              {accuracyHistory.length >= 2 && (
                <Card className="border-[#E5E7EB]" data-testid="accuracy-trend-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#5856D6]" />Accuracy Calibration Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <div className="lg:col-span-2">
                        <ResponsiveContainer width="100%" height={240}>
                          <LineChart data={accuracyHistory}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                            <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} tickFormatter={v => `${v}%`} />
                            <Tooltip formatter={(value, name) => {
                              const labels = { overall_accuracy: 'Accuracy', accurate_count: 'Accurate', close_count: 'Close', missed_count: 'Missed' };
                              return [name === 'overall_accuracy' ? `${value}%` : value, labels[name] || name];
                            }} />
                            <Line type="monotone" dataKey="overall_accuracy" name="overall_accuracy" stroke="#5856D6" strokeWidth={2.5} dot={{ fill: '#5856D6', r: 4 }} activeDot={{ r: 6 }} />
                            <Legend formatter={(value) => {
                              const labels = { overall_accuracy: 'Accuracy %' };
                              return labels[value] || value;
                            }} wrapperStyle={{ fontSize: 11 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="space-y-2">
                        <h4 className="text-xs font-semibold text-[#0A0A0A] mb-2">Report History</h4>
                        <div className="max-h-[200px] overflow-y-auto space-y-1.5 pr-1">
                          {[...accuracyHistory].reverse().map((h, i) => {
                            const accColor = h.overall_accuracy >= 70 ? '#34C759' : h.overall_accuracy >= 40 ? '#FF9500' : '#FF3B30';
                            const scenarioIcon = h.landed_scenario === 'Best Case' ? '↑' : h.landed_scenario === 'Worst Case' ? '↓' : '→';
                            return (
                              <div key={i} className="p-2 rounded-lg border border-[#E5E7EB] bg-white flex items-center gap-2" data-testid={`accuracy-history-${i}`}>
                                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: accColor + '15', color: accColor }}>
                                  {h.overall_accuracy}%
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[10px] text-[#0A0A0A] font-medium">{h.date}</p>
                                  <p className="text-[9px] text-[#9CA3AF]">{scenarioIcon} {h.landed_scenario} · {h.accurate_count}A {h.close_count}C {h.missed_count}M</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {accuracyHistory.length >= 2 && (() => {
                          const latest = accuracyHistory[accuracyHistory.length - 1]?.overall_accuracy || 0;
                          const first = accuracyHistory[0]?.overall_accuracy || 0;
                          const delta = latest - first;
                          return (
                            <div className="p-2 rounded-lg border border-dashed bg-[#F9FAFB] text-center" style={{ borderColor: delta >= 0 ? '#34C759' : '#FF3B30' }} data-testid="accuracy-trend-delta">
                              <span className="text-xs font-bold" style={{ color: delta >= 0 ? '#34C759' : '#FF3B30' }}>
                                {delta >= 0 ? '+' : ''}{delta}%
                              </span>
                              <span className="text-[10px] text-[#9CA3AF] ml-1">since first report</span>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Predictive Insights */}
              {r.predictive_insights?.length > 0 && (
                <Card className="border-[#E5E7EB]" data-testid="predictive-insights-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-[#AF52DE]" />Predictive Insights</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {r.predictive_insights.map((insight, i) => {
                        const prob = insight.probability || 0;
                        const impactColor = insight.impact === 'high' ? '#FF3B30' : insight.impact === 'medium' ? '#FF9500' : '#34C759';
                        const probColor = prob >= 80 ? '#FF3B30' : prob >= 60 ? '#FF9500' : '#34C759';
                        return (
                          <div key={i} className="p-3 rounded-lg border border-[#E5E7EB] bg-white hover:shadow-sm transition-shadow" data-testid={`predictive-insight-${i}`}>
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="text-xs font-semibold text-[#0A0A0A] flex items-center gap-1.5">
                                <TrendingUp className="w-3.5 h-3.5 text-[#AF52DE]" />
                                {insight.title}
                              </h4>
                              <div className="flex gap-1">
                                <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: probColor + '15', color: probColor }}>{prob}% likely</Badge>
                                <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: impactColor + '15', color: impactColor }}>{insight.impact}</Badge>
                              </div>
                            </div>
                            <p className="text-xs text-[#4B5563] mb-2 leading-relaxed">{insight.prediction}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] text-[#9CA3AF] flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{insight.timeframe}</span>
                              <span className="text-[10px] text-[#9CA3AF] italic">{insight.basis}</span>
                            </div>
                            {/* Probability bar */}
                            <div className="mt-2 w-full h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all" style={{ width: `${prob}%`, backgroundColor: probColor }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Key Drivers + Prescriptive Actions Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Key Drivers */}
                {r.key_drivers?.length > 0 && (
                  <Card className="border-[#E5E7EB]" data-testid="key-drivers-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2"><Flame className="w-4 h-4 text-[#FF9500]" />Key Drivers</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {[...r.key_drivers].sort((a, b) => (b.impact_score || 0) - (a.impact_score || 0)).map((driver, i) => {
                        const score = driver.impact_score || 0;
                        const isNeg = driver.direction === 'negative';
                        const barColor = isNeg ? '#FF3B30' : '#34C759';
                        return (
                          <div key={i} className="p-2.5 rounded-lg border border-[#E5E7EB] bg-white" data-testid={`key-driver-${i}`}>
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="text-xs font-semibold text-[#0A0A0A] flex items-center gap-1.5">
                                {isNeg ? <ArrowDownRight className="w-3 h-3 text-[#FF3B30]" /> : <ArrowUpRight className="w-3 h-3 text-[#34C759]" />}
                                {driver.driver}
                              </h4>
                              <span className="text-xs font-bold" style={{ color: barColor }}>{score.toFixed(1)}/10</span>
                            </div>
                            <p className="text-[10px] text-[#4B5563] mb-1.5">{driver.description}</p>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                                <div className="h-full rounded-full transition-all" style={{ width: `${score * 10}%`, backgroundColor: barColor }} />
                              </div>
                              <div className="flex gap-1">
                                {driver.affected_areas?.map((area, j) => (
                                  <Badge key={j} variant="outline" className="text-[8px] px-1 py-0 border-[#E5E7EB]">{area}</Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                )}

                {/* Prescriptive Actions */}
                {r.prescriptive_actions?.length > 0 && (
                  <Card className="border-[#E5E7EB]" data-testid="prescriptive-actions-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2"><Lightbulb className="w-4 h-4 text-[#007AFF]" />Prescriptive Actions</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {r.prescriptive_actions.map((action, i) => {
                        const conf = action.confidence || 0;
                        const priorityMap = { critical: { color: '#FF3B30', bg: '#FF3B30' }, high: { color: '#FF9500', bg: '#FF9500' }, medium: { color: '#007AFF', bg: '#007AFF' }, low: { color: '#34C759', bg: '#34C759' } };
                        const pri = priorityMap[action.priority] || priorityMap.medium;
                        const effortMap = { low: { label: 'Low Effort', color: '#34C759' }, medium: { label: 'Med Effort', color: '#FF9500' }, high: { label: 'High Effort', color: '#FF3B30' } };
                        const eff = effortMap[action.effort] || effortMap.medium;
                        const isCreated = !!createdActions[i];
                        const isCreating = creatingAction === i;
                        return (
                          <div key={i} className={`p-2.5 rounded-lg border-l-[3px] border bg-white ${isCreated ? 'border-[#34C759]/30 bg-[#34C759]/[0.02]' : 'border-[#E5E7EB]'}`} style={{ borderLeftColor: isCreated ? '#34C759' : pri.color }} data-testid={`prescriptive-action-${i}`}>
                            <div className="flex items-start justify-between mb-1">
                              <h4 className="text-xs font-semibold text-[#0A0A0A] flex-1 pr-2">{action.action}</h4>
                              <div className="flex gap-1 flex-shrink-0">
                                <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: pri.bg + '15', color: pri.color }}>{action.priority}</Badge>
                                <Badge className="text-[9px] px-1.5 py-0 bg-[#002FA7]/10 text-[#002FA7]">{conf}% conf.</Badge>
                              </div>
                            </div>
                            <p className="text-[10px] text-[#4B5563] mb-1.5">{action.expected_impact}</p>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-[10px] flex items-center gap-1" style={{ color: eff.color }}>
                                  <Gauge className="w-2.5 h-2.5" />{eff.label}
                                </span>
                                {action.kpi_target && (
                                  <span className="text-[10px] text-[#9CA3AF] flex items-center gap-1">
                                    <Target className="w-2.5 h-2.5" />{action.kpi_target}
                                  </span>
                                )}
                              </div>
                              {isCreated ? (
                                <span className="text-[10px] font-medium text-[#34C759] flex items-center gap-1" data-testid={`action-created-${i}`}>
                                  <CheckCircle className="w-3 h-3" />Task Created
                                </span>
                              ) : (
                                <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 border-[#002FA7]/30 text-[#002FA7] hover:bg-[#002FA7]/5"
                                  onClick={() => handleCreateActionTask(action, i)}
                                  disabled={isCreating}
                                  data-testid={`action-accept-btn-${i}`}>
                                  {isCreating ? <Loader2 className="w-2.5 h-2.5 animate-spin mr-1" /> : <Zap className="w-2.5 h-2.5 mr-1" />}
                                  {isCreating ? 'Creating...' : 'Accept & Create Task'}
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Scenario Comparisons */}
              {r.scenario_comparisons?.length > 0 && (
                <Card className="border-[#E5E7EB]" data-testid="scenario-comparisons-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2"><Layers className="w-4 h-4 text-[#5856D6]" />Scenario Comparisons</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Radar Chart */}
                      <div>
                        <ResponsiveContainer width="100%" height={280}>
                          <RadarChart data={[
                            { metric: 'Task Completion', ...Object.fromEntries(r.scenario_comparisons.map(s => [s.scenario, s.task_completion || 0])) },
                            { metric: 'Knowledge Health', ...Object.fromEntries(r.scenario_comparisons.map(s => [s.scenario, s.knowledge_health || 0])) },
                            { metric: 'Overdue Reduction', ...Object.fromEntries(r.scenario_comparisons.map(s => [s.scenario, s.overdue_reduction || 0])) },
                            { metric: 'Compliance', ...Object.fromEntries(r.scenario_comparisons.map(s => [s.scenario, s.compliance_score || 0])) },
                          ]}>
                            <PolarGrid stroke="#E5E7EB" />
                            <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10, fill: '#4B5563' }} />
                            <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9 }} />
                            {r.scenario_comparisons.map((s, idx) => {
                              const colors = ['#34C759', '#007AFF', '#FF3B30'];
                              return <Radar key={idx} name={s.scenario} dataKey={s.scenario} stroke={colors[idx] || '#999'} fill={colors[idx] || '#999'} fillOpacity={0.1} strokeWidth={2} />;
                            })}
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Tooltip />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                      {/* Scenario Cards */}
                      <div className="space-y-2">
                        {r.scenario_comparisons.map((s, i) => {
                          const scenarioStyles = {
                            'Best Case': { color: '#34C759', icon: TrendingUp },
                            'Expected': { color: '#007AFF', icon: Target },
                            'Worst Case': { color: '#FF3B30', icon: TrendingDown },
                          };
                          const style = scenarioStyles[s.scenario] || { color: '#9CA3AF', icon: CircleDot };
                          const Icon = style.icon;
                          return (
                            <div key={i} className="p-3 rounded-lg border border-[#E5E7EB] bg-white" data-testid={`scenario-${i}`}>
                              <div className="flex items-center gap-2 mb-2">
                                <Icon className="w-4 h-4" style={{ color: style.color }} />
                                <h4 className="text-xs font-bold" style={{ color: style.color }}>{s.scenario}</h4>
                              </div>
                              <div className="grid grid-cols-4 gap-2 mb-2">
                                {[
                                  { label: 'Tasks', value: s.task_completion },
                                  { label: 'Health', value: s.knowledge_health },
                                  { label: 'Overdue Fix', value: s.overdue_reduction },
                                  { label: 'Compliance', value: s.compliance_score },
                                ].map((m, j) => (
                                  <div key={j} className="text-center">
                                    <p className="text-sm font-bold" style={{ color: style.color }}>{m.value || 0}%</p>
                                    <p className="text-[9px] text-[#9CA3AF]">{m.label}</p>
                                  </div>
                                ))}
                              </div>
                              <p className="text-[10px] text-[#4B5563] leading-relaxed">{s.description}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </>
      ) : (
        /* Manual Report View */
        <Card className="border-[#E5E7EB]" data-testid="manual-report-view">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2"><FileBarChart className="w-5 h-5 text-[#4B5563]" />{r.title}</CardTitle>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" className="text-[#FF3B30]" onClick={() => handleDelete(r.report_id)}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-4">
              {r.report_type && <Badge variant="secondary" className="text-xs">{r.report_type}</Badge>}
              {r.region && <Badge variant="secondary" className="text-xs">{r.region}</Badge>}
              <span className="text-xs text-[#9CA3AF]">{r.created_at?.substring(0, 10)}</span>
            </div>
            <div className="text-sm text-[#4B5563] whitespace-pre-wrap leading-relaxed">{r.content}</div>
          </CardContent>
        </Card>
      )}

      {/* Manual Create Dialog */}
      <Dialog open={isManualOpen} onOpenChange={setIsManualOpen}>
        <DialogContent data-testid="manual-report-dialog">
          <DialogHeader><DialogTitle>Create Manual Report</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title *</Label><Input value={manualForm.title} onChange={e => setManualForm({ ...manualForm, title: e.target.value })} data-testid="report-title-input" /></div>
            <div><Label>Content</Label><Textarea value={manualForm.content} onChange={e => setManualForm({ ...manualForm, content: e.target.value })} rows={5} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label><Select value={manualForm.report_type} onValueChange={v => setManualForm({ ...manualForm, report_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="general">General</SelectItem><SelectItem value="financial">Financial</SelectItem><SelectItem value="operational">Operational</SelectItem><SelectItem value="compliance">Compliance</SelectItem></SelectContent></Select></div>
              <div><Label>Region</Label><Input value={manualForm.region} onChange={e => setManualForm({ ...manualForm, region: e.target.value })} placeholder="e.g., North America" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsManualOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleManualCreate}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      {shareOpen && r && (
        <ShareViaEmail open={shareOpen} onClose={() => setShareOpen(false)} contentType="report" contentId={r.report_id} contentTitle={r.title} />
      )}
    </div>
  );
};

export default ReportsPage;
